import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Send, Sparkles, User, Loader2, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { useChatStore, useProjectStore, useUIStore } from '../lib/store';
import { useTranslation } from '../lib/i18n';
import { toast } from 'sonner';

const ChatPage = () => {
  const { messages, sendMessage, fetchHistory, clearMessages, isLoading } = useChatStore();
  const { currentProject } = useProjectStore();
  const { language } = useUIStore();
  const { t } = useTranslation(language);
  
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    fetchHistory(currentProject?.id).catch(console.error);
  }, [currentProject?.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const message = inputValue;
    setInputValue('');
    
    try {
      await sendMessage(message, currentProject?.id);
    } catch (error) {
      toast.error(error.message || t('error'));
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      handleSend(e);
    }
  };

  return (
    <div className="h-full flex flex-col" data-testid="chat-page">
      {/* Header */}
      <div className="p-4 border-b border-white/10 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-xl font-bold flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            {t('chat')}
          </h1>
          {currentProject && (
            <p className="text-sm text-muted-foreground mt-1">
              {language === 'hi' ? 'प्रोजेक्ट:' : 'Project:'} {currentProject.name}
            </p>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            clearMessages();
            toast.success(language === 'hi' ? 'चैट साफ़ हो गई' : 'Chat cleared');
          }}
          data-testid="clear-chat-btn"
        >
          <Trash2 className="h-4 w-4 mr-2" />
          {language === 'hi' ? 'साफ़ करें' : 'Clear'}
        </Button>
      </div>

      {/* Messages Area */}
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-4 max-w-3xl mx-auto">
          {messages.length === 0 ? (
            <div className="text-center py-20">
              <div className="h-20 w-20 rounded-2xl bg-primary/20 flex items-center justify-center mx-auto mb-6">
                <Sparkles className="h-10 w-10 text-primary" />
              </div>
              <h2 className="font-heading text-2xl font-bold mb-2">
                {language === 'hi' ? 'OSLO One AI' : 'OSLO One AI'}
              </h2>
              <p className="text-muted-foreground max-w-md mx-auto">
                {language === 'hi' 
                  ? 'मुझे बताएं आप क्या करना चाहते हैं। प्रोजेक्ट बनाएं, टास्क जोड़ें, या अपने काम को व्यवस्थित करें।'
                  : 'Tell me what you want to do. Create projects, add tasks, or organize your work.'}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-8 max-w-lg mx-auto">
                {[
                  language === 'hi' ? '"एक नया प्रोजेक्ट बनाओ"' : '"Create a new project"',
                  language === 'hi' ? '"मेरे टास्क दिखाओ"' : '"Show my tasks"',
                  language === 'hi' ? '"रिपोर्ट बनाओ"' : '"Generate a report"',
                  language === 'hi' ? '"टास्क जोड़ो"' : '"Add a task"',
                ].map((suggestion, i) => (
                  <button
                    key={i}
                    onClick={() => setInputValue(suggestion.replace(/"/g, ''))}
                    className="p-3 rounded-xl bg-secondary/50 border border-white/10 hover:border-primary/50 text-sm text-left transition-colors"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((message, index) => (
              <motion.div
                key={message.id || index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex items-start gap-3 ${
                  message.role === 'user' ? 'flex-row-reverse' : ''
                }`}
              >
                <div className={`h-9 w-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                  message.role === 'user' ? 'bg-accent' : 'bg-primary'
                }`}>
                  {message.role === 'user' ? (
                    <User className="h-5 w-5 text-accent-foreground" />
                  ) : (
                    <Sparkles className="h-5 w-5 text-primary-foreground" />
                  )}
                </div>
                <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                  message.role === 'user' 
                    ? 'bg-accent/20 rounded-tr-none' 
                    : 'bg-primary/10 rounded-tl-none'
                }`}>
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {message.created_at ? new Date(message.created_at).toLocaleTimeString() : ''}
                  </p>
                </div>
              </motion.div>
            ))
          )}
          
          {isLoading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex items-start gap-3"
            >
              <div className="h-9 w-9 rounded-full bg-primary flex items-center justify-center">
                <Sparkles className="h-5 w-5 text-primary-foreground" />
              </div>
              <div className="bg-primary/10 rounded-2xl rounded-tl-none px-4 py-3">
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm text-muted-foreground">
                    {language === 'hi' ? 'सोच रहा हूं...' : 'Thinking...'}
                  </span>
                </div>
              </div>
            </motion.div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Input Area */}
      <div className="p-4 border-t border-white/10">
        <form onSubmit={handleSend} className="max-w-3xl mx-auto">
          <div className="glass rounded-full flex items-center gap-2 px-4 py-2">
            <Input
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('chat_placeholder')}
              className="flex-1 bg-transparent border-0 focus-visible:ring-0 text-base"
              disabled={isLoading}
              data-testid="chat-input"
            />
            <Button
              type="submit"
              size="icon"
              disabled={!inputValue.trim() || isLoading}
              className="rounded-full h-10 w-10 glow-primary"
              data-testid="chat-send-btn"
            >
              {isLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Send className="h-5 w-5" />
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChatPage;
